export type IntegrationStageDateRange = {
  startDateFrom: string | null;
  startDateTo: string | null;
  endDateFrom: string | null;
  endDateTo: string | null;
};

export const VALIDATION_STAGE_NAME = "Validation";

export type IntegrationStageFilterFields = {
  integrationStatuses: string[];
  validationStatuses: string[];
  validationScoreOperator: string;
  validationScoreValue: string;
  endorsementLevel: string[];
  dateRange: IntegrationStageDateRange;
};

export const EMPTY_STAGE_DATE_RANGE: IntegrationStageDateRange = {
  startDateFrom: null,
  startDateTo: null,
  endDateFrom: null,
  endDateTo: null,
};

export const EMPTY_STAGE_FILTER: IntegrationStageFilterFields = {
  integrationStatuses: [],
  validationStatuses: [],
  validationScoreOperator: ">",
  validationScoreValue: "",
  endorsementLevel: [],
  dateRange: { ...EMPTY_STAGE_DATE_RANGE },
};

export type IntegrationFilterState = {
  partnerType: string[];
  region: string[];
  consultantId: string[];
  technology: string[];
  integrationType: string[];
  /** Summary-tile filters (not set from drawer Validation panel). */
  validationStatus: string[];
  integrationStages: string[];
  stageFilters: Record<string, IntegrationStageFilterFields>;
};

export const EMPTY_INTEGRATION_FILTER: IntegrationFilterState = {
  partnerType: [],
  region: [],
  consultantId: [],
  technology: [],
  integrationType: [],
  validationStatus: [],
  integrationStages: [],
  stageFilters: {},
};

export const isValidationStage = (stage: string): boolean =>
  stage.trim().toLowerCase() === VALIDATION_STAGE_NAME.toLowerCase();

export const getStageFilter = (
  filter: IntegrationFilterState,
  stage: string
): IntegrationStageFilterFields =>
  filter.stageFilters[stage] ?? {
    ...EMPTY_STAGE_FILTER,
    dateRange: { ...EMPTY_STAGE_DATE_RANGE },
  };

const hasDateRange = (range: IntegrationStageDateRange): boolean =>
  Boolean(
    range.startDateFrom ||
      range.startDateTo ||
      range.endDateFrom ||
      range.endDateTo
  );

export const stageFilterHasValues = (
  stage: string,
  fields: IntegrationStageFilterFields
): boolean => {
  if (isValidationStage(stage)) {
    return (
      fields.validationStatuses.length > 0 ||
      fields.endorsementLevel.length > 0 ||
      fields.validationScoreValue.trim().length > 0 ||
      hasDateRange(fields.dateRange)
    );
  }

  return (
    fields.integrationStatuses.length > 0 || hasDateRange(fields.dateRange)
  );
};

/** Keep integrationStages in sync when stage panel fields are cleared via tags. */
export const updateStageFilter = (
  filter: IntegrationFilterState,
  stage: string,
  fields: IntegrationStageFilterFields
): IntegrationFilterState => {
  if (!stageFilterHasValues(stage, fields)) {
    const nextStageFilters = { ...filter.stageFilters };
    delete nextStageFilters[stage];

    return {
      ...filter,
      integrationStages: filter.integrationStages.filter((item) => item !== stage),
      stageFilters: nextStageFilters,
    };
  }

  const nextStageFilters = {
    ...filter.stageFilters,
    [stage]: fields,
  };

  return {
    ...filter,
    integrationStages: filter.integrationStages.includes(stage)
      ? filter.integrationStages
      : [...filter.integrationStages, stage],
    stageFilters: nextStageFilters,
  };
};

export const pruneEmptyStageFilters = (
  filter: IntegrationFilterState
): IntegrationFilterState => {
  const nextStageFilters: Record<string, IntegrationStageFilterFields> = {};
  const nextStages: string[] = [];

  for (const stage of filter.integrationStages) {
    const fields = filter.stageFilters[stage];
    if (!fields || !stageFilterHasValues(stage, fields)) continue;
    nextStages.push(stage);
    nextStageFilters[stage] = fields;
  }

  return {
    ...filter,
    integrationStages: nextStages,
    stageFilters: nextStageFilters,
  };
};

export const hasActiveIntegrationFilters = (
  filter: IntegrationFilterState
): boolean => {
  const hasStageFilters = Object.entries(filter.stageFilters).some(
    ([stage, fields]) => stageFilterHasValues(stage, fields)
  );

  return (
    filter.partnerType.length > 0 ||
    filter.region.length > 0 ||
    filter.consultantId.length > 0 ||
    filter.technology.length > 0 ||
    filter.integrationType.length > 0 ||
    filter.validationStatus.length > 0 ||
    hasStageFilters
  );
};
